import { translate } from '../../i18n/index.js';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { membershipsApi, type MyMembership } from '../../api/memberships.api.js';
import { formatCurrency, Spinner, Tag } from '../../components/design/AtomFitUI.js';
import {
  MembershipPlanCards,
  membershipSelectionCopy,
} from '../../components/membership/MembershipPlanCards.js';
import { useMembershipCheckoutStore } from '../../store/membership-checkout.store.js';
import type { MembershipPlan } from '@atomfit/shared';

const localImmediateCancellation = import.meta.env.DEV;

export function MyMembership() {
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState<MyMembership[]>([]);
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<MyMembership | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const setPendingPlan = useMembershipCheckoutStore((state) => state.setPendingPlan);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    membershipsApi
      .my()
      .then((response) => {
        if (!cancelled) setMemberships(response.data.data);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPlansLoading(true);
    membershipsApi
      .plans()
      .then((response) => {
        if (!cancelled) setPlans(response.data.data);
      })
      .catch((err: Error) => {
        if (!cancelled) setPlansError(err.message);
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  function confirmCancelMembership(membership: MyMembership) {
    setCancellingId(membership.id);
    setActionError(null);
    membershipsApi
      .cancel(membership.id)
      .then(() => membershipsApi.my())
      .then((response) => {
        setMemberships(response.data.data);
        setCancelTarget(null);
      })
      .catch((err: Error) => setActionError(err.message))
      .finally(() => setCancellingId(null));
  }

  function selectMembershipPlan(plan: MembershipPlan) {
    setPendingPlan(plan);
    void navigate('/gyms?flow=membership');
  }

  return (
    <>
      <h1 style={{ fontSize: 30, fontWeight: 800, margin: '0 0 24px' }}>
        {translate('pages.customer.my_membership.my_membership.h1.membership')}
      </h1>
      {loading && (
        <div
          className="af-card"
          style={{ padding: 24, display: 'flex', gap: 12, alignItems: 'center' }}
        >
          <Spinner />
          {translate('pages.customer.my_membership.my_membership.div_card.loading_membership')}
        </div>
      )}
      {error && (
        <div className="af-error">
          {translate('pages.customer.my_membership.my_membership.div_error.error_membership')}{' '}
          {error}
        </div>
      )}
      {!loading && !error && memberships.length === 0 && (
        <p style={{ margin: '0 0 16px', color: 'var(--af-muted)' }}>
          {translate('pages.customer.my_membership.my_membership.p.membership_active')}
        </p>
      )}
      {actionError && (
        <div className="af-error" style={{ marginBottom: 16 }}>
          {translate('pages.customer.my_membership.my_membership.div_error.error')} {actionError}
        </div>
      )}
      {!loading && !error && (
        <MembershipPicker
          onSelectPlan={selectMembershipPlan}
          plans={plans}
          plansError={plansError}
          plansLoading={plansLoading}
        />
      )}
      <div style={{ display: 'grid', gap: 16 }}>
        {memberships.map((membership) => (
          <section className="af-card" key={membership.id} style={{ padding: 22 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              <div>
                <h2 style={{ margin: '0 0 4px', fontSize: 22 }}>{membership.plan.name}</h2>
                <div style={{ color: 'var(--af-muted)', fontSize: 13 }}>
                  {membership.tenant.name}
                </div>
                {membership.cancelAtPeriodEnd && (
                  <div
                    style={{
                      color: 'var(--af-danger)',
                      fontSize: 13,
                      fontWeight: 800,
                      marginTop: 8,
                    }}
                  >
                    {translate('pages.customer.my_membership.my_membership.div.membership_cancel')}
                    {membership.currentPeriodEnd
                      ? formatDate(membership.currentPeriodEnd)
                      : translate('pages.customer.my_membership.my_membership.div.copy')}
                    {'.'}
                  </div>
                )}
              </div>
              <Tag>{membership.tier}</Tag>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 10,
                marginTop: 18,
              }}
            >
              <Metric
                label={translate('pages.customer.my_membership.my_membership.metric.credit_edit')}
                value={formatCurrency(
                  membership.plan.creditAmountInSmallestUnit,
                  membership.plan.currency,
                )}
              />

              <Metric
                label={translate('pages.customer.my_membership.my_membership.metric.discount')}
                value={`${membership.plan.discountPercent}%`}
              />

              <Metric
                label={translate('pages.customer.my_membership.my_membership.metric.booking')}
                value={`${membership.plan.maxReservationMinutes} min`}
              />

              <Metric
                label={translate('pages.customer.my_membership.my_membership.metric.calendar')}
                value={`${membership.plan.bookingAdvanceDays} dní`}
              />
            </div>
            <h3 style={{ margin: '22px 0 10px', fontSize: 16 }}>
              {translate('pages.customer.my_membership.my_membership.h3.membership_gym')}
            </h3>
            <div style={{ display: 'grid', gap: 8 }}>
              {membership.tenant.gyms.map((gym) => (
                <Link
                  key={gym.id}
                  to={`/gyms/${gym.id}`}
                  className="af-outline-btn"
                  style={{
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    textDecoration: 'none',
                  }}
                >
                  <span>{gym.name}</span>
                  <span style={{ color: 'var(--af-dim)', fontSize: 12 }}>{gym.city}</span>
                </Link>
              ))}
            </div>
            <div style={{ borderTop: '1px solid var(--af-border)', marginTop: 18, paddingTop: 16 }}>
              <button
                className="af-outline-btn"
                type="button"
                onClick={() => setCancelTarget(membership)}
                disabled={
                  (membership.cancelAtPeriodEnd && !localImmediateCancellation) ||
                  cancellingId === membership.id
                }
                style={{
                  width: '100%',
                  justifyContent: 'center',
                  color:
                    membership.cancelAtPeriodEnd && !localImmediateCancellation
                      ? 'var(--af-muted)'
                      : 'var(--af-danger)',
                }}
              >
                {membership.cancelAtPeriodEnd && !localImmediateCancellation
                  ? translate(
                      'pages.customer.my_membership.my_membership.button_outline_btn.cancel_create',
                    )
                  : cancellingId === membership.id
                    ? translate(
                        'pages.customer.my_membership.my_membership.button_outline_btn.membership',
                      )
                    : localImmediateCancellation
                      ? translate(
                          'pages.customer.my_membership.my_membership.button_outline_btn.cancel',
                        )
                      : translate(
                          'pages.customer.my_membership.my_membership.button_outline_btn.membership_cancel',
                        )}
              </button>
            </div>
          </section>
        ))}
      </div>
      {cancelTarget && (
        <div
          className="af-profile-modal-backdrop"
          role="presentation"
          onClick={() => setCancelTarget(null)}
        >
          <div
            className="af-profile-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="af-membership-cancel-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2
              id="af-membership-cancel-title"
              style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800 }}
            >
              {translate('pages.customer.my_membership.my_membership.h2.membership_cancel')}
            </h2>
            <p
              style={{
                margin: '0 0 22px',
                color: 'var(--af-muted)',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {localImmediateCancellation
                ? translate('pages.customer.my_membership.my_membership.p.membership_cancel', {
                    value1: cancelTarget.plan.name,
                  })
                : cancelTarget.currentPeriodEnd
                  ? translate('pages.customer.my_membership.my_membership.p.membership_active_2', {
                      value1: cancelTarget.plan.name,
                      value2: formatDate(cancelTarget.currentPeriodEnd),
                    })
                  : translate('pages.customer.my_membership.my_membership.p.membership_cancel_2', {
                      value1: cancelTarget.plan.name,
                    })}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                className="af-outline-btn"
                type="button"
                style={{ padding: '10px 16px' }}
                onClick={() => setCancelTarget(null)}
              >
                {translate('pages.customer.my_membership.my_membership.button_outline_btn.back')}
              </button>
              <button
                className="af-primary-btn"
                type="button"
                style={{ padding: '10px 18px' }}
                onClick={() => confirmCancelMembership(cancelTarget)}
                disabled={cancellingId === cancelTarget.id}
              >
                {cancellingId === cancelTarget.id
                  ? translate('pages.customer.my_membership.my_membership.button_primary_btn.label')
                  : translate(
                      'pages.customer.my_membership.my_membership.button_primary_btn.membership_cancel',
                    )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function formatDate(value: Date | string) {
  return new Date(value).toLocaleDateString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function MembershipPicker({
  onSelectPlan,
  plans,
  plansError,
  plansLoading,
}: {
  onSelectPlan: (plan: MembershipPlan) => void;
  plans: MembershipPlan[];
  plansError: string | null;
  plansLoading: boolean;
}) {
  return (
    <section className="af-card af-membership-select-section" style={{ marginBottom: 18 }}>
      <div className="af-membership-select-intro">
        <h2>{membershipSelectionCopy.title}</h2>
        <p>{membershipSelectionCopy.description}</p>
      </div>

      {plansLoading && (
        <div className="af-checkout-membership-loading">
          <Spinner />
          {translate(
            'pages.customer.my_membership.membership_picker.div_checkout_membership_loading.loading_membership',
          )}
        </div>
      )}
      {plansError && (
        <div className="af-error">
          {translate('pages.customer.my_membership.membership_picker.div_error.error')} {plansError}
        </div>
      )}

      {!plansLoading && !plansError && (
        <MembershipPlanCards plans={plans} onChoose={onSelectPlan} showIncludedAmenities />
      )}
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ borderTop: '1px solid var(--af-border)', paddingTop: 10 }}>
      <div
        style={{
          color: 'var(--af-dim)',
          fontSize: 11,
          fontWeight: 800,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div style={{ fontWeight: 900, marginTop: 4 }}>{value}</div>
    </div>
  );
}
